export interface Article {
  id: string;
  title: string;
  excerpt: string;
  contentHtml: string;
  category: string;
  categorySlug: string;
  author: string;
  date: string;
  imageUrl: string;
  imageAlt: string;
  tags: string[];
  featured: boolean;
  readTime: string;
}

export interface Category {
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
  articleCount: number;
}

export const categories: Category[] = [
  {
    name: '四季风光',
    slug: 'scenery',
    description: '记录校园四季变换的自然美景，从春樱到冬雪，每一季都有独特的韵味',
    imageUrl: 'https://readdy.ai/api/search-image?query=Beautiful%20university%20campus%20landscape%20with%20cherry%20blossoms%20and%20historic%20buildings%2C%20spring%20season%2C%20warm%20sunlight%2C%20green%20trees%2C%20peaceful%20atmosphere%2C%20soft%20pastel%20colors%2C%20vibrant%20nature%20scene%2C%20clean%20composition%2C%20bright%20and%20airy&width=800&height=600&seq=cat-scenery-01&orientation=landscape',
    articleCount: 24,
  },
  {
    name: '青春故事',
    slug: 'stories',
    description: '那些年在校园里发生的温暖故事，关于友情、梦想与成长的点滴记忆',
    imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20campus%20life%20scene%20with%20students%20sitting%20on%20grass%20under%20sunlight%2C%20reading%20books%20and%20chatting%2C%20youthful%20atmosphere%2C%20golden%20hour%20lighting%2C%20nostalgic%20mood%2C%20warm%20tones%2C%20soft%20photography%20style%2C%20clean%20background&width=800&height=600&seq=cat-stories-01&orientation=landscape',
    articleCount: 18,
  },
  {
    name: '校园摄影',
    slug: 'photography',
    description: '用镜头捕捉校园里最美的瞬间，分享摄影技巧与取景心得',
    imageUrl: 'https://readdy.ai/api/search-image?query=Aesthetic%20campus%20photography%20scene%20with%20vintage%20camera%20on%20wooden%20desk%2C%20sunlight%20streaming%20through%20window%2C%20photo%20prints%20scattered%2C%20warm%20artistic%20atmosphere%2C%20soft%20natural%20lighting%2C%20cream%20and%20beige%20tones%2C%20clean%20composition&width=800&height=600&seq=cat-photo-01&orientation=landscape',
    articleCount: 15,
  },
  {
    name: '校园美食',
    slug: 'food',
    description: '探寻校园食堂与周边小店的美味，回味那些熟悉的味道',
    imageUrl: 'https://readdy.ai/api/search-image?query=Cozy%20campus%20cafeteria%20scene%20with%20warm%20lighting%2C%20wooden%20tables%2C%20various%20food%20dishes%20displayed%2C%20students%20enjoying%20meal%2C%20homey%20atmosphere%2C%20soft%20warm%20colors%2C%20clean%20and%20inviting%20interior&width=800&height=600&seq=cat-food-01&orientation=landscape',
    articleCount: 12,
  },
  {
    name: '建筑之美',
    slug: 'architecture',
    description: '漫步校园，欣赏那些承载历史与现代交融的建筑艺术',
    imageUrl: 'https://readdy.ai/api/search-image?query=Elegant%20university%20architecture%20with%20historic%20red%20brick%20buildings%2C%20ivy%20covered%20walls%2C%20arched%20windows%2C%20afternoon%20sunlight%2C%20peaceful%20campus%20path%2C%20classical%20design%2C%20warm%20earth%20tones%2C%20timeless%20beauty&width=800&height=600&seq=cat-arch-01&orientation=landscape',
    articleCount: 10,
  },
  {
    name: '毕业季',
    slug: 'graduation',
    description: '毕业不是终点，而是新的起点，记录那些告别与启程的时刻',
    imageUrl: 'https://readdy.ai/api/search-image?query=Graduation%20ceremony%20scene%20on%20sunny%20campus%2C%20students%20in%20black%20gowns%20throwing%20caps%20in%20air%2C%20joyful%20celebration%2C%20warm%20golden%20light%2C%20emotional%20atmosphere%2C%20bright%20blue%20sky%2C%20clean%20composition&width=800&height=600&seq=cat-grad-01&orientation=landscape',
    articleCount: 8,
  },
];

export const articles: Article[] = [
  {
    id: 'article-1',
    title: '春日樱花季：校园最美赏樱路线全攻略',
    excerpt: '每年三月，校园里的樱花如约绽放，粉白色的花海将整个校园装点得如梦似幻。跟随我们的镜头，一起探寻校园最美的樱花打卡地。',
    contentHtml: `<p>每年三月中旬，校园里的数百株樱花树便迎来了它们最灿烂的时刻。从南门入口的樱花大道，到图书馆后的小径，再到操场边的樱花林，整座校园仿佛被一层粉白色的轻纱笼罩。</p>
<h2>最佳赏樱路线</h2>
<p>从南门进入校园，首先映入眼帘的是长达500米的樱花大道。两侧的染井吉野樱在三月中旬进入盛花期，粉白的花瓣在春风中摇曳，落英缤纷的景象令人陶醉。建议清晨或傍晚时分前来，阳光透过花瓣洒落地面，光影斑驳，最适合拍照。</p>
<h2>隐藏的赏樱宝地</h2>
<p>除了樱花大道，图书馆后面的小径也是绝佳的赏樱地点。这里的樱花树龄较大，枝干虬曲，花朵更加繁密。坐在树下的长椅上，捧一本书，偶尔有花瓣飘落在书页间，是校园生活中最诗意的时刻。</p>
<h2>摄影小贴士</h2>
<p>拍摄樱花时，建议使用大光圈镜头营造浅景深效果，让前景与背景的樱花形成层次感。逆光拍摄时，花瓣会呈现半透明的质感，格外梦幻。如果遇到微风天气，可以尝试慢速快门捕捉花瓣飘落的动态美。</p>`,
    category: '四季风光',
    categorySlug: 'scenery',
    author: '林小春',
    date: '2026-03-15',
    imageUrl: 'https://readdy.ai/api/search-image?query=Stunning%20cherry%20blossom%20avenue%20on%20university%20campus%2C%20pink%20petals%20falling%20in%20spring%20breeze%2C%20historic%20campus%20buildings%20in%20background%2C%20soft%20warm%20sunlight%2C%20dreamy%20atmosphere%2C%20clean%20natural%20composition%2C%20pastel%20color%20palette&width=1200&height=800&seq=article-1-hero&orientation=landscape',
    imageAlt: '校园樱花大道春日盛开的美丽景色',
    tags: ['樱花', '春天', '校园风光', '摄影攻略'],
    featured: true,
    readTime: '8分钟',
  },
  {
    id: 'article-2',
    title: '图书馆的午后：那些与书香为伴的温暖时光',
    excerpt: '午后阳光透过图书馆的落地窗洒在书页上，空气中弥漫着淡淡的纸墨香。这里承载了无数青春的梦想与奋斗的足迹。',
    contentHtml: `<p>校园图书馆，是每个学子心中最温暖的角落。那栋红砖建筑静静矗立在校园中心，见证了一届又一届学子的成长。每天午后两点，阳光会准时透过三楼的落地窗，将温暖洒满整个阅览室。</p>
<h2>图书馆的一天</h2>
<p>清晨七点，图书馆门前已经排起了长队。考研党们抱着厚厚的复习资料，在晨光中等待开馆。管理员阿姨准时推开厚重的木门，新一天的学习生活就此开始。到了午后，阳光透过玻璃穹顶洒下柔和的光线，整个大厅都笼罩在温暖的金色中。</p>
<h2>最受欢迎的角落</h2>
<p>三楼的靠窗位置是图书馆最抢手的座位。窗外是古老的银杏树，春天嫩绿，夏天葱郁，秋天金黄，冬天则是一树白雪映衬着红墙，四季皆有不同的风景。在这里，时间仿佛放慢了脚步，只剩下书页翻动的声音和笔尖划过纸面的沙沙声。</p>`,
    category: '青春故事',
    categorySlug: 'stories',
    author: '陈墨白',
    date: '2026-04-02',
    imageUrl: 'https://readdy.ai/api/search-image?query=Warm%20library%20interior%20with%20sunlight%20streaming%20through%20large%20windows%2C%20wooden%20bookshelves%2C%20reading%20tables%2C%20students%20studying%20peacefully%2C%20golden%20afternoon%20light%2C%20cozy%20atmosphere%2C%20warm%20beige%20and%20wood%20tones&width=1200&height=800&seq=article-2-hero&orientation=landscape',
    imageAlt: '午后阳光洒满图书馆阅览室的温暖场景',
    tags: ['图书馆', '阅读', '青春记忆', '校园生活'],
    featured: true,
    readTime: '6分钟',
  },
  {
    id: 'article-3',
    title: '秋日银杏：校园最美的金色时光',
    excerpt: '当秋风染黄了银杏叶，整条银杏大道便成了金色的童话世界。这是校园四季中最璀璨的篇章，也是一年中最适合漫步思考的季节。',
    contentHtml: `<p>每年的十月末到十一月中，是校园银杏最美的时节。银杏大道两旁的百年银杏树换上了金色的盛装，在秋日的暖阳下熠熠生辉。这条不足千米的道路，承载了无数校园情侣的甜蜜回忆，也见证了无数学子从青涩走向成熟。</p>
<h2>银杏大道的四季</h2>
<p>春天的银杏大道，嫩绿的扇形叶片在微风中轻轻摇曳；夏天浓密的树荫为行人遮挡烈日；秋天的金黄是最令人期待的盛景；而冬天光秃的枝干在白雪映衬下也别有一番韵味。</p>
<h2>金色地毯</h2>
<p>深秋时节，金黄的银杏叶铺满整条道路，踩上去沙沙作响。很多同学会特意绕道经过这里，用手机记录下这短暂而绚烂的美景。还有一些摄影爱好者会架起三脚架，耐心等待最佳的光线和构图。</p>`,
    category: '四季风光',
    categorySlug: 'scenery',
    author: '林小春',
    date: '2025-11-08',
    imageUrl: 'https://readdy.ai/api/search-image?query=Golden%20ginkgo%20tree%20avenue%20on%20university%20campus%20in%20autumn%2C%20yellow%20leaves%20covering%20the%20ground%2C%20warm%20sunlight%20filtering%20through%20branches%2C%20historic%20red%20brick%20buildings%2C%20peaceful%20walking%20path%2C%20nostalgic%20seasonal%20beauty&width=1200&height=800&seq=article-3-hero&orientation=landscape',
    imageAlt: '秋日校园银杏大道金色落叶的美丽景色',
    tags: ['银杏', '秋天', '校园风光', '金色季节'],
    featured: true,
    readTime: '7分钟',
  },
  {
    id: 'article-4',
    title: '深夜食堂：校园后街那些暖胃又暖心的小店',
    excerpt: '每当夜幕降临，校园后街的烟火气便开始升腾。那些不起眼的小店，用一道道简单却温暖的食物，慰藉着离家在外的学子们。',
    contentHtml: `<p>校园后街，是一条不足三百米的小巷，却汇聚了天南海北的美食。从东北的饺子到四川的麻辣烫，从广东的肠粉到新疆的大盘鸡，每一家小店都有自己忠实的粉丝。而对于很多学生来说，这些小店的意义早已超越了填饱肚子——它们是深夜赶作业后的慰藉，是考试结束后的庆祝，更是离家千里时最接近家的味道。</p>
<h2>深夜的灵魂食物</h2>
<p>凌晨一点，老张烧烤摊前依然排着长队。炭火的香气混合着孜然的味道在夜空中飘散，几串羊肉串、一份烤茄子，配上一瓶冰镇饮料，就是最完美的深夜搭配。老张在这里摆了十五年摊，送走了一届又一届毕业生。</p>`,
    category: '校园美食',
    categorySlug: 'food',
    author: '王知味',
    date: '2026-05-20',
    imageUrl: 'https://readdy.ai/api/search-image?query=Cozy%20night%20street%20food%20scene%20near%20campus%2C%20small%20restaurants%20with%20warm%20yellow%20lights%2C%20steaming%20food%20stalls%2C%20students%20enjoying%20late%20night%20snacks%2C%20nostalgic%20atmosphere%2C%20warm%20orange%20and%20amber%20tones%2C%20lively%20yet%20intimate&width=1200&height=800&seq=article-4-hero&orientation=landscape',
    imageAlt: '校园后街深夜小吃摊温暖热闹的场景',
    tags: ['校园美食', '深夜食堂', '后街小吃'],
    featured: false,
    readTime: '10分钟',
  },
  {
    id: 'article-5',
    title: '那些年，我们在操场上流过汗水和泪水',
    excerpt: '操场是校园里最有活力的地方——清晨的晨跑、午后的体育课、傍晚的社团训练，每一寸跑道都烙印着青春的印记。',
    contentHtml: `<p>校园操场，从清晨六点到晚上十点，几乎没有安静的时刻。这里是汗水与欢笑交织的地方，也是无数青春故事上演的舞台。红色塑胶跑道、绿色草坪、高高的看台，构成了校园生活中最生动的画面。</p>
<h2>运动会的记忆</h2>
<p>每年四月的校运动会是操场最热闹的时候。各院系的方阵表演、激烈的百米冲刺、令人屏息的接力赛，整个操场都被加油呐喊声淹没。跑道边，后勤同学忙着递水递毛巾；看台上，拉拉队挥舞着院旗为自己学院的选手助威。</p>`,
    category: '青春故事',
    categorySlug: 'stories',
    author: '陈墨白',
    date: '2026-04-18',
    imageUrl: 'https://readdy.ai/api/search-image?query=University%20sports%20field%20on%20sunny%20day%2C%20students%20running%20on%20red%20track%2C%20green%20grass%20field%2C%20sports%20activities%2C%20youthful%20energy%2C%20bright%20natural%20lighting%2C%20vibrant%20colors%2C%20clean%20wide%20composition&width=1200&height=800&seq=article-5-hero&orientation=landscape',
    imageAlt: '阳光明媚的校园操场运动场景',
    tags: ['操场', '运动会', '青春活力', '校园生活'],
    featured: false,
    readTime: '7分钟',
  },
  {
    id: 'article-6',
    title: '手机也能拍大片：校园风光手机摄影技巧',
    excerpt: '不需要昂贵的单反相机，一部手机就足以记录校园的美好。本文分享十个实用的手机摄影技巧，让你的校园照片瞬间提升质感。',
    contentHtml: `<p>很多同学觉得拍出好看的照片一定要用专业相机，其实只要掌握一些简单的技巧，手机也能拍出令人惊艳的校园风光大片。以下是我在校园摄影中总结的十个实用技巧。</p>
<h2>光线是照片的灵魂</h2>
<p>摄影就是用光作画。校园摄影最好的时间段是日出后一小时和日落前一小时，也就是摄影师常说的"黄金时刻"。这时候的光线柔和温暖，能够给建筑和人物镀上一层迷人的金色光晕。正午的强烈顶光则容易造成过曝和生硬的阴影，尽量避免在这个时段拍摄。</p>
<h2>构图的黄金法则</h2>
<p>打开手机相机的网格线功能，将主体放在三分线的交叉点上，这是最简单也最有效的构图方法。拍摄建筑时，注意保持垂直线条的竖直；拍摄风景时，可以利用道路、栏杆等引导线将观众的视线引向主体。</p>`,
    category: '校园摄影',
    categorySlug: 'photography',
    author: '林小春',
    date: '2026-06-05',
    imageUrl: 'https://readdy.ai/api/search-image?query=Smartphone%20photography%20on%20campus%2C%20person%20holding%20phone%20taking%20photo%20of%20beautiful%20campus%20scenery%2C%20golden%20hour%20warm%20light%2C%20creative%20composition%2C%20clean%20minimal%20style%2C%20soft%20natural%20colors&width=1200&height=800&seq=article-6-hero&orientation=landscape',
    imageAlt: '用手机拍摄校园风光的场景示意',
    tags: ['手机摄影', '摄影技巧', '校园风光', '构图'],
    featured: false,
    readTime: '12分钟',
  },
  {
    id: 'article-7',
    title: '老教学楼的光影故事：穿越百年的时光走廊',
    excerpt: '走进那栋有着百年历史的老教学楼，阳光穿过拱形窗户，在走廊里投下斑驳的光影。每一块青砖、每一扇木窗都在诉说着岁月的故事。',
    contentHtml: `<p>在校园的最深处，有一栋建于上世纪二十年代的老教学楼。青砖灰瓦、拱形门窗、木质楼梯，处处透露着民国建筑的典雅气质。虽然现在大部分课程已经搬到了新教学楼，但这栋老楼依然是校园里最具魅力的建筑之一。</p>
<h2>光影走廊</h2>
<p>老教学楼最迷人的地方是三楼的走廊。南侧一整排拱形窗户，每到下午时分，阳光斜斜地穿过窗棂，在地上投射出规则的光影图案。走在这样的走廊里，仿佛穿行在时光隧道中，耳边似乎还能听到几十年前学子们的脚步声和读书声。</p>`,
    category: '建筑之美',
    categorySlug: 'architecture',
    author: '陈墨白',
    date: '2026-07-12',
    imageUrl: 'https://readdy.ai/api/search-image?query=Historic%20university%20building%20corridor%20with%20arched%20windows%2C%20warm%20sunlight%20casting%20beautiful%20shadows%20on%20wooden%20floor%2C%20classical%20architecture%2C%20vintage%20atmosphere%2C%20cream%20and%20warm%20beige%20tones%2C%20peaceful%20and%20timeless&width=1200&height=800&seq=article-7-hero&orientation=landscape',
    imageAlt: '百年老教学楼走廊光影斑驳的美丽场景',
    tags: ['老建筑', '教学楼', '光影', '历史建筑'],
    featured: false,
    readTime: '6分钟',
  },
  {
    id: 'article-8',
    title: '不说再见：毕业季最感人的十个瞬间',
    excerpt: '又是一年毕业季，校园里弥漫着栀子花的香气和离别的愁绪。让我们用镜头记录下这些珍贵的告别时刻，致我们终将逝去的青春。',
    contentHtml: `<p>六月的校园，栀子花开得正盛，而离别的钟声也已敲响。学士服、毕业照、散伙饭、最后的班会……每一个瞬间都充满了不舍与祝福。作为校园的记录者，我用镜头捕捉了毕业季最感人的十个瞬间。</p>
<h2>学士服下的青春</h2>
<p>穿上期盼了四年的学士服，站在校门口合影的那一刻，很多人才突然意识到——真的要离开了。镜头前，大家努力挤出最灿烂的笑容，可眼眶却不由自主地泛红。四年的时光太快，快到还没来得及好好逛一遍校园的每一个角落。</p>`,
    category: '毕业季',
    categorySlug: 'graduation',
    author: '林小春',
    date: '2026-06-20',
    imageUrl: 'https://readdy.ai/api/search-image?query=University%20graduation%20scene%2C%20students%20in%20black%20graduation%20gowns%20smiling%20with%20tears%2C%20holding%20diplomas%2C%20campus%20background%20with%20gardenia%20flowers%2C%20emotional%20warm%20atmosphere%2C%20golden%20sunset%20light%2C%20nostalgic%20beauty&width=1200&height=800&seq=article-8-hero&orientation=landscape',
    imageAlt: '毕业季学生穿着学士服在校园合影的感人场景',
    tags: ['毕业季', '离别', '青春', '感动'],
    featured: true,
    readTime: '10分钟',
  },
  {
    id: 'article-9',
    title: '冬日初雪：校园银装素裹的静谧之美',
    excerpt: '当第一场雪悄然降临，平日里熟悉的校园仿佛变成了一个纯净的童话世界。红色的教学楼、白色的雪地，构成了最经典的冬日校园画面。',
    contentHtml: `<p>南方的雪总是格外珍贵。每年冬天的第一场雪，总能让整个校园沸腾——南方同学兴奋地冲出宿舍看雪，北方同学则淡定地在一旁微笑。记得去年那场大雪，一夜之间，整个校园变成了银装素裹的世界。</p>
<h2>雪中校园</h2>
<p>红墙黛瓦的老建筑在白雪的映衬下更显古朴典雅。操场上的足球门框挂满了冰凌，在阳光下闪烁着晶莹的光芒。湖面结了一层薄冰，几片枯叶被封在冰层中，像是一幅自然的艺术画。</p>`,
    category: '四季风光',
    categorySlug: 'scenery',
    author: '林小春',
    date: '2026-01-10',
    imageUrl: 'https://readdy.ai/api/search-image?query=University%20campus%20covered%20in%20fresh%20snow%2C%20historic%20red%20brick%20buildings%20with%20white%20snow%20contrast%2C%20bare%20trees%20with%20frost%2C%20peaceful%20winter%20scene%2C%20soft%20diffused%20light%2C%20clean%20minimal%20composition%2C%20serene%20atmosphere&width=1200&height=800&seq=article-9-hero&orientation=landscape',
    imageAlt: '冬日校园银装素裹的美丽雪景',
    tags: ['冬雪', '雪景', '校园风光', '冬季'],
    featured: false,
    readTime: '5分钟',
  },
  {
    id: 'article-10',
    title: '宿舍楼下的那只橘猫：校园流浪猫的生活日常',
    excerpt: '每所大学都有自己的"校猫"。我们宿舍楼下那只橘猫，陪伴了一届又一届的同学，早已成为校园里不可或缺的一员。',
    contentHtml: `<p>大黄是我们宿舍楼下的一只橘猫。没有人知道它是什么时候出现在校园里的，但几乎所有住过这栋楼的同学都认识它。它有着圆滚滚的身材和一双琥珀色的眼睛，性格慵懒而亲人，最喜欢在阳光好的午后躺在宿舍门口的台阶上晒太阳。</p>
<h2>大黄的日常</h2>
<p>每天早上七点半，大黄会准时出现在食堂后门，等着好心的食堂阿姨给它准备早餐。吃饱喝足后，它会慢悠悠地踱步到教学楼前的草坪上，找一个最舒服的位置开始一天的"工作"——晒太阳。路过的同学们总会忍不住停下来摸摸它，而它也来者不拒，眯着眼睛享受着大家的爱抚。</p>`,
    category: '青春故事',
    categorySlug: 'stories',
    author: '陈墨白',
    date: '2026-08-01',
    imageUrl: 'https://readdy.ai/api/search-image?query=Cute%20orange%20tabby%20cat%20lying%20on%20university%20campus%20steps%20in%20warm%20sunlight%2C%20peaceful%20scene%2C%20green%20plants%20around%2C%20cozy%20atmosphere%2C%20soft%20natural%20lighting%2C%20warm%20beige%20and%20cream%20tones%2C%20clean%20composition&width=1200&height=800&seq=article-10-hero&orientation=landscape',
    imageAlt: '校园里慵懒晒太阳的橘猫温馨场景',
    tags: ['校园猫咪', '流浪猫', '温暖日常'],
    featured: false,
    readTime: '6分钟',
  },
];

export const featuredArticles = articles.filter(a => a.featured);

export function getArticlesByCategory(slug: string): Article[] {
  return articles.filter(a => a.categorySlug === slug);
}

export function getArticleById(id: string): Article | undefined {
  return articles.find(a => a.id === id);
}

export const faqData = [
  {
    question: '校园春色网站主要分享什么内容？',
    answer: '校园春色是一个专注于记录和分享校园美好生活的平台。我们主要分享校园四季风光摄影、青春校园故事、摄影技巧教程、校园美食探访、建筑之美赏析以及毕业季特别策划等内容，致力于为每一位热爱校园生活的朋友提供温暖而有深度的内容。',
  },
  {
    question: '如何投稿自己的校园摄影作品或故事？',
    answer: '我们非常欢迎同学们投稿分享自己拍摄的校园作品和故事！你可以通过网站底部的联系表单提交作品，也可以发送邮件到我们的投稿邮箱。投稿时请附上作品标题、简介以及你的联系方式，我们的编辑团队会在3个工作日内回复。',
  },
  {
    question: '网站上的图片可以下载使用吗？',
    answer: '网站上的所有图片均受版权保护。如果你是非商业用途的个人使用（如手机壁纸、朋友圈分享等），请保留我们的水印并注明出处。如需商业使用或高清原图，请通过联系方式与我们取得联系获取授权。',
  },
  {
    question: '校园春色的更新频率是怎样的？',
    answer: '我们每周更新2-3篇原创内容，涵盖摄影作品、校园故事、美食探访等不同主题。在樱花季、银杏季、毕业季等特殊时节，更新频率会更高，确保第一时间为大家带来最新的校园动态和最美的校园风光。',
  },
  {
    question: '如何第一时间获取网站的最新内容？',
    answer: '你可以关注我们的社交媒体账号（微信公众号、小红书等）获取最新内容推送。也可以订阅我们的邮件通讯，我们会定期将精选内容发送到你的邮箱。此外，建议将网站加入浏览器收藏夹，随时回来看看校园的最新变化。',
  },
];

export const aboutData = {
  brandIntro: {
    title: '关于校园春色',
    content: '校园春色创立于2023年春天，是一个由一群热爱校园生活的年轻人发起的原创内容平台。我们相信，每一座校园都有它独特的美——春天樱花树下的浪漫、夏天梧桐树荫里的清凉、秋天银杏大道上的金色、冬天初雪覆盖的静谧。我们用心记录这些美好瞬间，与每一位怀念或正在经历校园生活的你分享。',
    mission: '用镜头和文字，让每一帧校园春色都成为永恒的记忆。',
  },
  reputation: {
    stats: [
      { label: '原创文章', value: '200+' },
      { label: '摄影作品', value: '1500+' },
      { label: '活跃读者', value: '50,000+' },
      { label: '合作高校', value: '30+' },
    ],
    testimonials: [
      { text: '校园春色让我毕业后依然能感受到母校的温暖，每次看到新文章都像是回了一趟学校。', author: '小李，2024届校友' },
      { text: '作为在校生，校园春色是我每周必看的网站。它让我发现了校园里很多被忽略的美。', author: '小王，大三学生' },
      { text: '这里的摄影作品水准很高，我从中学到了很多拍摄技巧，现在也成了校园摄影爱好者。', author: '小张，摄影爱好者' },
    ],
  },
  contact: {
    email: 'hello@qinglanriyu.cn',
    wechat: '校园春色Official',
    xiaohongshu: '校园春色',
    address: '中国·北京·海淀区大学城',
  },
};